/* eslint-disable unicorn/prefer-string-slice */

/* eslint-disable sonarjs/cognitive-complexity */
import React from 'react'

import {
  ChangeMeta,
  InputAttributes,
  NumberFormatBaseProps,
  PatternFormatProps,
} from './types'
import { useBaseMask } from './useBaseMask'
import {
  charIsAny,
  charIsNumber,
  charIsString,
  getCaretPosInBoundary,
  getDefaultChangeMeta,
  getMaskAtIndex,
  noop,
  setCaretPosition,
} from './utils'

// eslint-disable-next-line unicorn/prefer-set-has
const patterns = ['d', 'l', '#']

const extractAny = (str: string) => str.replace(/[^\dA-Za-zА-я]/g, '')

export function format<BaseType = InputAttributes>(
  numStr: string,
  props: PatternFormatProps<BaseType>,
) {
  const format = props.format as string
  const { allowEmptyFormatting, mask } = props

  if (numStr === '' && !allowEmptyFormatting) return ''

  let hashCount = 0
  // eslint-disable-next-line unicorn/prefer-spread
  const formattedNumberAry = format.split('')
  for (let i = 0, ln = format.length; i < ln; i++) {
    if (patterns.includes(format[i])) {
      formattedNumberAry[i] =
        numStr[hashCount] || getMaskAtIndex(mask, hashCount)
      hashCount += 1
    }
  }
  return formattedNumberAry.join('')
}

export function removeFormatting<BaseType = InputAttributes>(
  value: string,
  changeMeta: ChangeMeta = getDefaultChangeMeta(value),
  props: PatternFormatProps<BaseType>,
) {
  const format = props.format as string
  const { from, to, lastValue = '' } = changeMeta
  const isNumericSlot = (caretPos: number) => format[caretPos] === 'd'
  const isStringSlot = (caretPos: number) => format[caretPos] === 'l'
  const isAnySlot = (caretPos: number) => format[caretPos] === '#'

  const removeFormatChar = (string: string, startIndex: number) => {
    let str = ''
    // eslint-disable-next-line unicorn/no-for-loop
    for (let i = 0; i < string.length; i++) {
      if (isNumericSlot(startIndex + i) && charIsNumber(string[i])) {
        str += string[i]
      }
      if (isStringSlot(startIndex + i) && charIsString(string[i])) {
        str += string[i]
      }
      if (isAnySlot(startIndex + i) && charIsAny(string[i])) {
        str += string[i]
      }
    }
    return str
  }

  if (lastValue === '' && value.length === format.length) {
    let str = ''
    // eslint-disable-next-line unicorn/no-for-loop
    for (let i = 0; i < value.length; i++) {
      if (isNumericSlot(i) && charIsNumber(value[i])) {
        str += value[i]
      }
      if (isStringSlot(i) && charIsString(value[i])) {
        str += value[i]
      }
      if (isAnySlot(i) && charIsAny(value[i])) {
        str += value[i]
      }
    }

    return str
  }

  const firstSection = lastValue.substring(0, from.start)
  const middleSection = value.substring(to.start, to.end)
  const lastSection = lastValue.substring(from.end)

  return `${removeFormatChar(firstSection, 0)}${extractAny(
    middleSection,
  )}${removeFormatChar(lastSection, from.end)}`
}

export function getCaretBoundary<BaseType = InputAttributes>(
  formattedValue: string,
  props: PatternFormatProps<BaseType>,
) {
  const format = props.format as string
  const { mask } = props
  const boundaryAry = Array.from({ length: formattedValue.length + 1 }).map(
    () => true,
  )

  let hashCount = 0
  let firstEmptySlot = -1
  const maskAndIndexMap: Record<number, string | undefined> = {}

  // eslint-disable-next-line unicorn/prefer-spread
  format.split('').forEach((char, index) => {
    // eslint-disable-next-line unicorn/no-useless-undefined
    let maskAtIndex = undefined
    if (patterns.includes(char)) {
      hashCount++
      maskAtIndex = getMaskAtIndex(mask, hashCount - 1)
      if (firstEmptySlot === -1 && formattedValue[index] === maskAtIndex) {
        firstEmptySlot = index
      }
    }

    maskAndIndexMap[index] = maskAtIndex
  })

  const isPosAllowed = (pos: number) => {
    return (
      patterns.includes(format[pos]) &&
      formattedValue[pos] !== maskAndIndexMap[pos]
    )
  }

  for (let i = 0, ln = boundaryAry.length; i < ln; i++) {
    boundaryAry[i] =
      i === firstEmptySlot || isPosAllowed(i) || isPosAllowed(i - 1)
  }

  return boundaryAry
}

export function usePattern<BaseType = InputAttributes>(
  props: PatternFormatProps<BaseType>,
): NumberFormatBaseProps<BaseType> {
  const { format: formatProp, onKeyDown = noop, ...restProps } = props

  const _getCaretBoundary = (formattedValue: string) => {
    return getCaretBoundary(formattedValue, props)
  }

  const _onKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { key } = e
    // eslint-disable-next-line unicorn/consistent-destructuring
    const el = e.target as HTMLInputElement | HTMLTextAreaElement
    const { selectionStart, selectionEnd, value } = el

    if (selectionStart !== selectionEnd) {
      onKeyDown(e)
      return
    }

    let caretPos = selectionStart || 0

    if (key === 'Backspace' || key === 'Delete') {
      let direction
      if (key === 'Backspace') {
        while (caretPos > 0 && !patterns.includes(formatProp[caretPos - 1])) {
          caretPos--
        }
        direction = 'left'
      } else {
        const formatLn = formatProp.length
        while (
          caretPos < formatLn &&
          !patterns.includes(formatProp[caretPos])
        ) {
          caretPos++
        }
        direction = 'right'
      }

      caretPos = getCaretPosInBoundary(
        value,
        caretPos,
        _getCaretBoundary(value),
        direction,
      )
    } else if (
      !patterns.includes(formatProp[caretPos]) &&
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight'
    ) {
      caretPos = getCaretPosInBoundary(
        value,
        caretPos + 1,
        _getCaretBoundary(value),
        'right',
      )
    }

    if (caretPos !== selectionStart) {
      setCaretPosition(el, caretPos)
    }

    onKeyDown(e)
  }

  return {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    ...(restProps as NumberFormatBaseProps<BaseType>),
    format: (numStr: string) => format(numStr, props),
    removeFormatting: (inputValue: string, changeMeta: ChangeMeta) =>
      removeFormatting(inputValue, changeMeta, props),
    getCaretBoundary: _getCaretBoundary,
    onKeyDown: _onKeyDown,
  }
}

export function usePatternMask<BaseType = InputAttributes>(
  props: PatternFormatProps<BaseType>,
) {
  const patternFormatProps = usePattern(props)
  return useBaseMask(patternFormatProps)
}
